import moment from 'moment';

const DURATION_TOKEN_MAP: Record<string, (d: moment.Duration) => number> = {
  D: (d) => Math.floor(d.asDays()) || 0,
  DD: (d) => Math.floor(d.asDays()) || 0,

  H: (d) => d.hours() || 0,
  HH: (d) => d.hours() || 0,

  m: (d) => d.minutes() || 0,
  mm: (d) => d.minutes() || 0,

  s: (d) => d.seconds() || 0,
  ss: (d) => d.seconds() || 0,
};

/**
 * Moment.js Duration objesini özel format string'i kullanarak formatlar.
 *
 * Format string'i özel token'lar ve bloklar içerir:
 * - `{...}` blokları: Koşullu bloklar. İçindeki tüm token'ların değeri 0 ise, tüm blok gösterilmez.
 * - `[...]` token'ları: Duration değerleriyle değiştirilen token'lar.
 *
 * @param duration - Formatlanacak moment.js Duration objesi
 * @param format - Format string'i. Özel token'lar ve bloklar içerebilir.
 *
 * @returns Formatlanmış süre string'i
 *
 * ## Desteklenen Token'lar:
 *
 * - **D, DD**: Gün (0-∞)
 *   - `D`: Tek haneli (örn: "5")
 *   - `DD`: İki haneli, sıfırla doldurulmuş (örn: "05")
 *
 * - **H, HH**: Saat (0-23)
 *   - `H`: Tek haneli (örn: "12")
 *   - `HH`: İki haneli, sıfırla doldurulmuş (örn: "12")
 *
 * - **m, mm**: Dakika (0-59)
 *   - `m`: Tek haneli (örn: "5")
 *   - `mm`: İki haneli, sıfırla doldurulmuş (örn: "05")
 *
 * - **s, ss**: Saniye (0-59)
 *   - `s`: Tek haneli (örn: "5")
 *   - `ss`: İki haneli, sıfırla doldurulmuş (örn: "05")
 *
 * ## Format String Syntax:
 *
 * 1. **Koşullu Bloklar `{...}`**:
 *    - İçindeki tüm `[...]` token'larının değeri 0 ise, tüm blok çıktıdan çıkarılır.
 *    - Örnek: `"{[DD] gün}"` - Eğer gün 0 ise, bu blok gösterilmez.
 *
 * 2. **Token'lar `[...]`**:
 *    - Köşeli parantez içindeki token, duration değeriyle değiştirilir.
 *    - Token tanınmıyorsa, olduğu gibi gösterilir.
 *
 * 3. **Boşluklar**:
 *    - Birden fazla boşluk tek boşluğa indirgenir.
 *    - Başta ve sonda boşluklar temizlenir.
 *
 * @example
 * // Basit kullanım - tüm değerleri göster
 * formatDurationString(duration, '[DD] gün [HH] saat [mm] dakika [ss] saniye')
 * // Çıktı: "05 gün 12 saat 30 dakika 45 saniye"
 *
 * @example
 * // Koşullu bloklar - 0 olan değerler gösterilmez
 * formatDurationString(duration, '{[DD] gün} {[HH] saat} {[mm] dakika} {[ss] saniye}')
 * // Eğer gün 0 ise: "12 saat 30 dakika 45 saniye"
 * // Eğer saat de 0 ise: "30 dakika 45 saniye"
 * // Eğer dakika da 0 ise: "45 saniye"
 *
 * @example
 * // Tek karakterli token'lar - değer olduğu gibi gösterilir
 * formatDurationString(duration, '[D] gün [H] saat')
 * // Çıktı: "5 gün 12 saat"
 *
 * @example
 * // İki karakterli token'lar - 2 haneli formatlanır (padStart ile)
 * formatDurationString(duration, '[DD] gün [HH] saat [mm] dakika')
 * // Çıktı: "05 gün 12 saat 30 dakika"
 *
 * @example
 * // Karmaşık örnek
 * const duration = moment.duration({ days: 2, hours: 0, minutes: 5, seconds: 30 });
 * formatDurationString(duration, '{[DD] gün} {[HH] saat} {[mm] dakika} {[ss] saniye}')
 * // Çıktı: "02 gün 05 dakika 30 saniye" (saat 0 olduğu için gösterilmedi)
 */
export default function formatDurationString(duration: moment.Duration, format: string) {
  return format
    .replace(/\{([^}]+)\}/g, (_, block) => {
      const tokens = block.match(/\[([^\]]+)\]/g) || [];

      const hasNonZero = tokens.some((t: string) => {
        const key = t.slice(1, -1);
        const fn = DURATION_TOKEN_MAP[key];
        return fn ? fn(duration) > 0 : false;
      });

      if (!hasNonZero) return '';

      return block.replace(/\[([^\]]+)\]/g, (_: any, token: string) => {
        const fn = DURATION_TOKEN_MAP[token];
        if (!fn) return token;

        const value = fn(duration);
        return token.length === 2 ? String(value).padStart(2, '0') : String(value);
      });
    })

    .replace(/\[([^\]]+)\]/g, (_: any, token: string) => {
      const fn = DURATION_TOKEN_MAP[token];
      if (!fn) return token;

      const value = fn(duration);
      return token.length === 2 ? String(value).padStart(2, '0') : String(value);
    })
    .replace(/\s+/g, ' ')
    .trim();
}
